import { useQuery } from "@apollo/client";
import { CheckCircle } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ApproverContact,
  GetCampaignForApproverQuery,
  GetCampaignForApproverQueryVariables,
  RequestApproverNumbersMutation,
  RequestApproverNumbersMutationVariables,
  SubmitApproverApprovalsMutation,
  SubmitApproverApprovalsMutationVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import {
  GET_CAMPAIGN_FOR_APPROVER,
  REQUEST_APPROVER_NUMBERS,
  SUBMIT_APPROVER_APPROVALS,
} from "~frontend/graphql/campaigns";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";

type ApproverState = "ready" | "approving" | "success";

function ApproverContent(): ReactElement {
  const intl = useIntl();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [state, setState] = useState<ApproverState>("ready");
  const [contacts, setContacts] = useState<ApproverContact[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [approvedIds, setApprovedIds] = useState<string[]>([]);

  // Fetch campaign data
  const { loading, data, error } = useQuery<
    GetCampaignForApproverQuery,
    GetCampaignForApproverQueryVariables
  >(GET_CAMPAIGN_FOR_APPROVER, {
    variables: {
      input: {
        campaignId: campaignId || "",
      },
    },
  });

  const [requestNumbers, { loading: requestingNumbers }] = useMutation<
    RequestApproverNumbersMutation,
    RequestApproverNumbersMutationVariables
  >(REQUEST_APPROVER_NUMBERS, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to request numbers",
      description:
        "Error message when requesting approval numbers fails in approver mode",
    }),
  });

  const [submitApprovals, { loading: submittingApprovals }] = useMutation<
    SubmitApproverApprovalsMutation,
    SubmitApproverApprovalsMutationVariables
  >(SUBMIT_APPROVER_APPROVALS, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to submit approvals",
      description:
        "Error message when submitting approvals fails in approver mode",
    }),
    refetchQueries: [GET_CAMPAIGN_FOR_APPROVER],
    awaitRefetchQueries: true,
  });

  return (
    <PageContainer>
      <AsyncWrapper loading={loading} errors={error} data={data?.getCampaign}>
        {campaign => {
          async function handleRequestNumbers(): Promise<void> {
            const { data: numbersData } = await requestNumbers({
              variables: {
                input: {
                  campaignId: campaign.id,
                },
              },
            });

            if (numbersData?.requestApproverNumbers) {
              setContacts(numbersData.requestApproverNumbers);
              setCurrentIndex(0);
              setApprovedIds([]);
              setState("approving");
            }
          }

          function handleApprove(): void {
            const currentContact = contacts[currentIndex];
            const newApprovedIds = [...approvedIds, currentContact.id];
            setApprovedIds(newApprovedIds);

            if (currentIndex === contacts.length - 1) {
              // Last contact, submit approvals
              void handleSubmitApprovals(newApprovedIds);
            } else {
              // Move to next contact
              setCurrentIndex(currentIndex + 1);
            }
          }

          async function handleSubmitApprovals(ids: string[]): Promise<void> {
            const { errors } = await submitApprovals({
              variables: {
                input: {
                  campaignId: campaign.id,
                  approvalIds: ids,
                },
              },
            });

            if (!errors) {
              setState("success");
            }
          }

          // Ready state
          if (
            state === "ready" &&
            campaign.clickerApprovals.required >
              campaign.clickerApprovals.granted
          ) {
            return (
              <FlexBox
                direction="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
                sx={{ mt: 8 }}
              >
                <Typography variant="h2">
                  {intl.formatMessage({
                    defaultMessage: "Ready to approve contacts to text?",
                    description:
                      "Heading prompting user to start approving contacts in approver mode",
                  })}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRequestNumbers}
                  disabled={requestingNumbers}
                  sx={{ minWidth: "200px" }}
                >
                  {requestingNumbers
                    ? intl.formatMessage({
                        defaultMessage: "Loading...",
                        description:
                          "Loading state text when requesting numbers in approver mode",
                      })
                    : intl.formatMessage({
                        defaultMessage: "Request Numbers",
                        description:
                          "Button text to request approval numbers in approver mode",
                      })}
                </Button>
              </FlexBox>
            );
          }

          // Approving state
          if (state === "approving" && contacts.length > 0) {
            const currentContact = contacts[currentIndex];
            const approvedCount = approvedIds.length;

            return (
              <FlexBox
                direction="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
                sx={{ mt: 8 }}
              >
                <Typography variant="h1">{currentContact.label}</Typography>
                <Typography variant="body1">{currentContact.detail}</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApprove}
                  disabled={submittingApprovals}
                  sx={{ minWidth: "200px" }}
                >
                  {intl.formatMessage({
                    defaultMessage: "Approve",
                    description:
                      "Button text to approve a contact in approver mode",
                  })}
                </Button>
                <Typography variant="body2">
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        "{approvedCount} of {totalCount} contacts approved",
                      description:
                        "Progress indicator showing how many contacts have been approved",
                    },
                    {
                      approvedCount,
                      totalCount: contacts.length,
                    }
                  )}
                </Typography>
              </FlexBox>
            );
          }

          // Success state or default fallback (all approvals complete)
          const canRequestMore = state === "success";
          const successMessage = canRequestMore
            ? intl.formatMessage({
                defaultMessage:
                  "All your contacts have been approved. Request more numbers to keep approving.",
                description:
                  "Success message when batch of contacts is approved and more can be requested",
              })
            : intl.formatMessage({
                defaultMessage:
                  "All contacts in this campaign have been approved.",
                description:
                  "Success message when all campaign contacts have been approved",
              });

          return (
            <FlexBox
              direction="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
              sx={{ mt: 8 }}
            >
              <FlexBox direction="row" alignItems="center" gap={1}>
                <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                <Typography variant="h1Bold">
                  {intl.formatMessage({
                    defaultMessage: "Success!",
                    description: "Success heading in approver mode",
                  })}
                </Typography>
              </FlexBox>
              <Typography variant="body1">{successMessage}</Typography>
              {canRequestMore && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRequestNumbers}
                  disabled={requestingNumbers}
                  sx={{ minWidth: "200px" }}
                >
                  {requestingNumbers
                    ? intl.formatMessage({
                        defaultMessage: "Loading...",
                        description:
                          "Loading state text when requesting more numbers in approver mode",
                      })
                    : intl.formatMessage({
                        defaultMessage: "Request More Numbers",
                        description:
                          "Button text to request more approval numbers in approver mode",
                      })}
                </Button>
              )}
            </FlexBox>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}

export function Approver(): ReactElement {
  const intl = useIntl();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { subaccountLink } = useSubaccount()!;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch campaign data to validate access
  const { loading, error, data } = useQuery<
    GetCampaignForApproverQuery,
    GetCampaignForApproverQueryVariables
  >(GET_CAMPAIGN_FOR_APPROVER, {
    variables: {
      input: {
        campaignId: campaignId || "",
      },
    },
  });

  const campaign = data?.getCampaign;

  useBreadcrumbs(
    campaign
      ? [
          {
            label: campaign.name,
            path: subaccountLink(`/approve/${campaignId}`),
          },
        ]
      : []
  );

  // Handle errors (includes campaign not found and access denied)
  useEffect(() => {
    if (error) {
      enqueueSnackbar(
        intl.formatMessage({
          defaultMessage:
            "Campaign not found or you do not have access to this campaign",
          description:
            "Error message when campaign is not found or user lacks access in approver mode",
        }),
        {
          variant: "error",
        }
      );
      void navigate("/");
    }
  }, [error, enqueueSnackbar, navigate, intl]);

  return (
    <AsyncWrapper loading={loading} errors={error}>
      <ApproverContent />
    </AsyncWrapper>
  );
}
